import type { FormTemplateItem, FormTemplateSection } from '@/types/formsEngine';

/** How many labels a change line names before it says "and N more". */
const LABELS_SHOWN = 3;

function describe(verb: 'Removes' | 'Adds' | 'Changes', labels: string[]): string {
  const named = labels.slice(0, LABELS_SHOWN).join(', ');
  const rest = labels.length - LABELS_SHOWN;
  return `${verb} ${labels.length} ${labels.length === 1 ? 'question' : 'questions'}: ${named}${
    rest > 0 ? ` and ${rest} more` : ''
  }`;
}

/** What a customer sees of a question: moving or re-pinning it changes none of this. */
function customerFacing(item: FormTemplateItem): string {
  return JSON.stringify([
    item.label.trim(),
    item.type,
    item.required,
    item.description?.trim() || null,
    item.config?.options ?? [],
  ]);
}

/**
 * Which questions publishing `next` over `current` would remove, add and
 * change, matched by item id across all sections. A change is to what the
 * customer sees — label, type, required, help text or options — and is named
 * as it will read afterwards; a question that only moved is no change. Empty
 * when both hold the same questions.
 */
export function questionChanges(
  current: FormTemplateSection[],
  next: FormTemplateSection[],
): string[] {
  const currentItems = current.flatMap((section) => section.items);
  const nextItems = next.flatMap((section) => section.items);
  const currentIds = new Set(currentItems.map((item) => item.id));
  const nextIds = new Set(nextItems.map((item) => item.id));
  const removed = currentItems.filter((item) => !nextIds.has(item.id));
  const added = nextItems.filter((item) => !currentIds.has(item.id));
  const currentById = new Map(currentItems.map((item) => [item.id, item]));
  const changed = nextItems.filter((item) => {
    const before = currentById.get(item.id);
    return before !== undefined && customerFacing(before) !== customerFacing(item);
  });

  const changes: string[] = [];
  if (removed.length > 0) changes.push(describe('Removes', removed.map((item) => item.label)));
  if (added.length > 0) changes.push(describe('Adds', added.map((item) => item.label)));
  if (changed.length > 0) changes.push(describe('Changes', changed.map((item) => item.label)));
  return changes;
}
